/* ===========================================================================
   ELECTRO — FREE GIFT ENGINE
   ===========================================================================
   Brief: Jose, Slack 2026-09-08 4:11am. "When a customer selects the 4-pack or
   8-pack subscription in the buy widget, add the Hydration + Recovery Guide to
   the cart, quantity 1, $0, as a one-time line item with no selling plan.
   Remove it if they switch to one-time or to the 2-pack. Never let quantity
   exceed 1."

   ---------------------------------------------------------------------------
   WHY THIS IS DRIVEN BY THE CART AND NOT BY THE WIDGET'S RADIO BUTTONS
   ---------------------------------------------------------------------------
   The brief describes the behaviour from the shopper's seat ("when they select
   the 8-pack"), and the obvious reading is "fire an add on the radio click".
   That reading is wrong in a way that only shows up in the cart:

     * Selecting a pack in the widget puts NOTHING in the cart. Adding the gift
       on the click alone leaves a shopper who never pressed Add to Cart with a
       cart containing one free ebook and nothing else — visible in the header
       count, in the drawer, and in every abandoned-cart email the store sends.
     * The widget is not the only way a qualifying line gets into this cart. The
       creator pages run their own older widget, the drawer has a
       "SUBSCRIBE & SAVE" button that converts one-time lines in place, the
       drawer has pack-upgrade buttons, and the /cart page has its own quantity
       and remove controls. A widget-click hook is blind to all four.
     * A shopper can arrive with a cart from yesterday.

   So the rule implemented here is the invariant the brief is really asking for:

       THE GIFT IS PRESENT IF AND ONLY IF THE CART CONTAINS A QUALIFYING
       SUBSCRIPTION LINE. Quantity is always exactly 1. Never a selling plan.

   Every path above then works for free, including ones nobody thought to name,
   and the end state after "select 8-pack -> Add to cart" is exactly what Jose
   described. reconcile() is called after every cart mutation and on page load.

   ---------------------------------------------------------------------------
   THE RENEWAL TRAP THIS IS BUILT AROUND
   ---------------------------------------------------------------------------
   Jose: "it must not attach to the Stay AI subscription, or it re-adds every
   renewal." A line added with a selling_plan becomes part of the subscription
   contract and ships/bills forever. So the gift is added with NO selling_plan,
   and — because the drawer's own "convert everything to a subscription" button
   would otherwise sweep it up — a gift line that is somehow found carrying a
   plan is deleted and re-added clean rather than left in place. Defence in
   depth: the drawer is also patched to skip gift lines, but this engine does
   not trust that patch to still be there in six months.

   Depends on nothing. Config comes from window.ELECTRO_GIFT_DATA, emitted by
   snippets/electro-free-gift.liquid.
   =========================================================================== */
(function () {
  'use strict';

  var CFG = window.ELECTRO_GIFT_DATA;
  if (!CFG || !CFG.gifts) return;

  var ROUTES = CFG.routes || {};
  var CART_URL = ROUTES.cart || '/cart.js';
  var ADD_URL = ROUTES.add || '/cart/add.js';
  var CHANGE_URL = ROUTES.change || '/cart/change.js';

  /* Every configured gift variant, INCLUDING the ones currently switched off.
     A gift that gets disabled must still be recognised as a gift line, or the
     lines it already created would be invisible to this engine and would sit in
     carts forever with a stepper next to them. */
  var ALL_GIFT_VARIANTS = CFG.gifts
    .map(function (g) { return g.variantId; })
    .filter(function (id) { return !!id; });

  function log() {
    if (!CFG.debug || !window.console) return;
    try { console.log.apply(console, ['[electro-gift]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  /* ---------------------------------------------------------------------
     Reading the cart payload
     --------------------------------------------------------------------- */

  /* A line is a subscription when the AJAX API gives it a selling plan
     allocation. Same test the cart drawer uses — kept identical on purpose, so
     the two can never disagree about what "a subscription" means. */
  function lineIsSub(li) {
    return !!(li && li.selling_plan_allocation && li.selling_plan_allocation.selling_plan);
  }

  function isGiftLine(li) {
    return !!li && ALL_GIFT_VARIANTS.indexOf(li.variant_id) !== -1;
  }

  /* The gift rules that are live right now. A rule with no resolved variant id
     means the product behind it was deleted, unpublished or renamed — treat it
     as off rather than posting an add for variant `null`. */
  function activeGifts() {
    if (!CFG.enabled) return [];
    return CFG.gifts.filter(function (g) { return g.enabled && g.variantId; });
  }

  /* Does the cart earn this gift? A qualifying line is one of the pack variants
     listed for the rule AND carrying a selling plan. Both halves matter: the
     4-pack bought one-time earns nothing, and a 2-pack subscription earns
     nothing. Gift lines themselves are never counted, or two gifts could hold
     each other in the cart. */
  function qualifies(cart, g) {
    var ids = g.qualifying || [];
    for (var i = 0; i < cart.items.length; i++) {
      var li = cart.items[i];
      if (isGiftLine(li)) continue;
      if (!lineIsSub(li)) continue;
      if (ids.indexOf(li.variant_id) !== -1) return true;
    }
    return false;
  }

  /* variantId -> rule, for every gift the current cart has earned. */
  /* Two ladders (2026-10-07): "main" (every page) and "sorority" (carts flagged _gift_ladder=sorority by the sorority
     page). A cart only ever earns the gifts of its own ladder; gift lines of the other ladder are removed. */
  function ladderOf(cart) {
    return cart && cart.attributes && cart.attributes._gift_ladder === 'sorority' ? 'sorority' : 'main';
  }
  function desired(cart) {
    var want = {};
    var ladder = ladderOf(cart);
    activeGifts().forEach(function (g) {
      if ((g.ladder || 'main') !== ladder) return;
      if (qualifies(cart, g)) want[String(g.variantId)] = g;
    });
    return want;
  }

  /* ---------------------------------------------------------------------
     Planning
     ---------------------------------------------------------------------
     Produces the complete list of operations that turns the cart we were
     handed into the cart we want. It is computed in one shot from the whole
     payload rather than reacting to a single event, which is what makes a
     single pass sufficient and makes running it twice a no-op. */
  function planOps(cart) {
    var want = desired(cart);
    var ops = [];
    var kept = {};

    cart.items.forEach(function (li) {
      if (!isGiftLine(li)) return;
      var key = String(li.variant_id);
      var rule = want[key];

      /* Not earned any more (switched to one-time, dropped to the 2-pack,
         removed the pack, or the rule was switched off) -> take it out. */
      if (!rule) {
        ops.push({ op: 'remove', key: li.key, why: 'not earned' });
        return;
      }
      /* A duplicate line for the same gift. Shopify creates a separate line
         when the properties differ, so this is reachable — keep the first and
         drop the rest, because "never let quantity exceed 1" has to mean one
         unit in the cart, not one unit per line. */
      if (kept[key]) {
        ops.push({ op: 'remove', key: li.key, why: 'duplicate' });
        return;
      }
      /* A gift wearing a selling plan is the renewal bug. Do not try to strip
         the plan with change.js — that endpoint keeps the plan on the line.
         Remove it and let the add branch below rebuild it clean. */
      if (lineIsSub(li)) {
        ops.push({ op: 'remove', key: li.key, why: 'carried a selling plan' });
        return;
      }
      kept[key] = true;
      if (li.quantity !== 1) {
        ops.push({ op: 'qty', key: li.key, quantity: 1, why: 'quantity was ' + li.quantity });
      }
    });

    Object.keys(want).forEach(function (key) {
      if (kept[key]) return;
      ops.push({ op: 'add', id: parseInt(key, 10), rule: want[key], why: 'earned' });
    });

    return ops;
  }

  /* ---------------------------------------------------------------------
     Applying
     --------------------------------------------------------------------- */
  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().catch(function () { return {}; }).then(function (d) {
        throw new Error(d.description || d.message || 'cart update failed');
      });
      return r.json();
    });
  }

  function getCart() {
    return fetch(CART_URL, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart unavailable');
        return r.json();
      });
  }

  /* Line KEYS, never line indexes. An index shifts the moment a line above it
     is removed, so a two-op plan addressed by index deletes the wrong row. */
  function applyOp(op) {
    if (op.op === 'remove') return post(CHANGE_URL, { id: op.key, quantity: 0 });
    if (op.op === 'qty') return post(CHANGE_URL, { id: op.key, quantity: op.quantity });
    if (op.op === 'add') {
      var item = { id: op.id, quantity: 1 };
      /* NO selling_plan. Deliberate, load-bearing, and the whole point. */
      var props = { _free_gift: op.rule.key };
      /* The underscore prefix keeps it out of the customer-facing cart and
         checkout display while still landing on the order, so support and the
         fulfilment side can see why a $0 line is there. */
      item.properties = props;
      return post(ADD_URL, { items: [item] });
    }
    return Promise.resolve();
  }

  /* ---------------------------------------------------------------------
     reconcile()
     ---------------------------------------------------------------------
     Single-flight. Two callers firing at once (the widget's add and the
     drawer's refresh both land within a frame of each other) would otherwise
     each read the pre-change cart, each decide the gift is missing, and each
     add one. Chaining means the second sees the first one's result.

     Never rejects. A cart that cannot be reconciled must still be a cart the
     shopper can check out with — a thrown error here would take the drawer's
     render down with it. */
  var chain = Promise.resolve();

  function runOnce(preloaded) {
    var start = preloaded ? Promise.resolve(preloaded) : getCart();
    return start.then(function (cart) {
      if (!cart || !cart.items) return { cart: cart, changed: false };
      var ops = planOps(cart);
      if (!ops.length) {
        log('nothing to do', cart.items.length + ' lines');
        return { cart: cart, changed: false };
      }
      log('ops', ops.map(function (o) { return o.op + ':' + o.why; }).join(', '));
      var applied = 0;
      return ops.reduce(function (p, op) {
        return p.then(function () {
          return applyOp(op).then(function () { applied++; });
        });
      }, Promise.resolve())
        .catch(function (e) { log('op failed', e && e.message); })
        .then(getCart)
        .then(function (fresh) { return { cart: fresh, changed: applied > 0 }; });
    }).catch(function (e) {
      log('reconcile failed', e && e.message);
      return { cart: null, changed: false };
    });
  }

  function reconcile(preloaded) {
    chain = chain.then(function () { return runOnce(preloaded); });
    return chain;
  }

  /* ---------------------------------------------------------------------
     Helpers the drawer and the cart page use to keep gifts out of the maths
     --------------------------------------------------------------------- */

  /* The lines the shopper actually chose. Jose: "Exclude it from all widget and
     cart price math." The gift is $0 so it moves no money, but it does move
     the item COUNT, and it breaks "is this an all-subscription cart?" — which
     is what decides the drawer's heading and its free-shipping copy. */
  function shoppingItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(function (li) { return !isGiftLine(li); });
  }

  function shoppingCount(cart) {
    return shoppingItems(cart).reduce(function (n, li) { return n + li.quantity; }, 0);
  }

  function giftItems(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.filter(isGiftLine);
  }

  function ruleFor(li) {
    for (var i = 0; i < CFG.gifts.length; i++) {
      if (CFG.gifts[i].variantId === li.variant_id) return CFG.gifts[i];
    }
    return null;
  }

  window.ElectroGifts = {
    reconcile: reconcile,
    isGiftLine: isGiftLine,
    lineIsSub: lineIsSub,
    shoppingItems: shoppingItems,
    shoppingCount: shoppingCount,
    giftItems: giftItems,
    ruleFor: ruleFor,
    planOps: planOps,
    config: CFG
  };

  /* ---------------------------------------------------------------------
     Mount
     ---------------------------------------------------------------------
     On every page, once. The drawer calls reconcile() itself inside its
     refresh(), so on drawer pages this initial pass is usually a no-op; it
     exists for the /cart page, which has no drawer, and for any page where a
     stale cart needs cleaning before the shopper looks at it.

     On /cart the markup is server-rendered Liquid, so a cart we just changed is
     being displayed from stale HTML. Reload once — and only when something
     actually changed, which is why applyOp counts successes rather than
     assuming. The session counter is a second belt: if an add silently never
     sticks (a product unpublished mid-session, say), the page must not reload
     forever. */
  function mount() {
    reconcile().then(function (res) {
      if (!res.changed || !CFG.isCartPage) return;
      var n = 0;
      try { n = parseInt(sessionStorage.getItem('electro_gift_reload') || '0', 10) || 0; } catch (e) {}
      if (n >= 2) { log('reload budget spent'); return; }
      try { sessionStorage.setItem('electro_gift_reload', String(n + 1)); } catch (e) {}
      window.location.reload();
    });
  }

  /* Clear the reload budget on any page that is not the cart, so a later visit
     to /cart starts with a fresh allowance. */
  if (!CFG.isCartPage) {
    try { sessionStorage.removeItem('electro_gift_reload'); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
